import { supabase } from '../lib/supabase';

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  role: 'user' | 'coordinator' | 'admin';
}

export const authService = {
  // Login user with email and password using Supabase Auth
  async login(email: string, password: string): Promise<{ success: boolean; user?: AuthUser; error?: string }> {
    try {
      // Try to sign in with Supabase Auth
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email: email,
        password: password,
      });

      if (authError) {
        console.error('Auth error:', authError);
        return { success: false, error: 'Invalid email or password.' };
      }

      if (!authData.user) {
        return { success: false, error: 'Authentication failed.' };
      }

      // Get user data from our users table
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('*')
        .eq('email', email)
        .single();

      if (userError || !userData) {
        return { success: false, error: 'User profile not found.' };
      }

      // Check if user has coordinator or admin role
      const { data: profile } = await supabase
        .from('coordinator_profiles')
        .select('role, status')
        .eq('user_email', email)
        .eq('status', 'active')
        .single();

      let role: 'user' | 'coordinator' | 'admin' = 'user';
      if (profile) {
        role = profile.role;
      }

      const authUser: AuthUser = {
        id: userData.id,
        email: userData.email,
        name: userData.name,
        role: role
      };

      return { success: true, user: authUser };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: 'Login failed. Please try again.' };
    }
  },

  // Get current authenticated user from Supabase session
  async getCurrentUser(): Promise<AuthUser | null> {
    try {
      // Get current session from Supabase
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session?.user) {
        return null;
      }

      const email = session.user.email;
      if (!email) {
        return null;
      }

      // Get user data from our users table
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('*')
        .eq('email', email)
        .single();

      if (userError || !userData) {
        return null;
      }

      // Check if user has coordinator or admin role in database
      const { data: profile, error } = await supabase
        .from('coordinator_profiles')
        .select('role, status')
        .eq('user_email', email)
        .eq('status', 'active')
        .single();

      let role: 'user' | 'coordinator' | 'admin' = 'user';
      
      if (profile && !error) {
        role = profile.role;
      }

      return {
        id: userData.id,
        email: userData.email,
        name: userData.name,
        role: role
      };
    } catch (error) {
      console.error('Error getting current user:', error);
      return null;
    }
  },

  // Logout user
  async logout(): Promise<void> {
    try {
      await supabase.auth.signOut();
      // Also clear localStorage for compatibility
      localStorage.removeItem('gomtb_user');
      localStorage.removeItem('gomtb_current_user_id');
      localStorage.removeItem('gomtb_current_user_email');
    } catch (error) {
      console.error('Logout error:', error);
    }
  },

  // Check if current user can access coordinator features
  async canAccessCoordinatorFeatures(): Promise<boolean> {
    const user = await this.getCurrentUser();
    return user?.role === 'coordinator' || user?.role === 'admin';
  },

  // Check if current user can access admin features
  async canAccessAdminFeatures(): Promise<boolean> {
    const user = await this.getCurrentUser();
    return user?.role === 'admin';
  },

  // Get coordinator ID for filtering events
  async getCurrentCoordinatorId(): Promise<string | null> {
    const user = await this.getCurrentUser();
    if (user?.role === 'coordinator' || user?.role === 'admin') {
      return user.id;
    }
    return null;
  },

  // Create or update user role in coordinator_profiles table
  async setUserRole(userEmail: string, userName: string, userId: string, role: 'coordinator' | 'admin'): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('coordinator_profiles')
        .upsert({
          user_id: userId,
          user_email: userEmail,
          user_name: userName,
          role: role,
          status: 'active',
          total_credits_purchased: role === 'admin' ? 999 : 0,
          first_upgrade_date: new Date().toISOString(),
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_email'
        });

      if (error) {
        console.error('Error setting user role:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in setUserRole:', error);
      return false;
    }
  },

  // Initialize admin and coordinator users in Supabase Auth
  async initializeSystemUsers(): Promise<void> {
    try {
      const systemUsers = [
        {
          email: 'admin@gomtb.co.za',
          password: 'admin123',
          name: 'GOMTB Admin',
          id: '00000000-0000-4000-8000-000000000001',
          role: 'admin' as const
        },
        {
          email: 'coordinator@gomtb.co.za',
          password: 'coord123',
          name: 'Event Coordinator',
          id: '00000000-0000-4000-8000-000000000002',
          role: 'coordinator' as const
        }
      ];

      for (const user of systemUsers) {
        try {
          // Store password in localStorage for compatibility
          localStorage.setItem(`gomtb_password_${user.email}`, user.password);

          // Create user in database if not exists
          const { data: existingUser } = await supabase
            .from('users')
            .select('id')
            .eq('email', user.email)
            .single();

          if (!existingUser) {
            await supabase
              .from('users')
              .insert({
                id: user.id,
                email: user.email,
                name: user.name,
                phone: '+27123456789',
                date_of_birth: '1990-01-01',
                gender: 'prefer_not_to_say',
                rider_category: 'admin',
                emergency_contact_name: 'System Admin',
                emergency_contact_phone: '+27123456789',
                emergency_contact_relationship: 'system',
                created_at: new Date().toISOString()
              });
          }

          // Create coordinator profile
          await this.setUserRole(user.email, user.name, user.id, user.role);

          // Try to create user in Supabase Auth
          try {
            const { error: signUpError } = await supabase.auth.signUp({
              email: user.email,
              password: user.password,
              options: {
                data: {
                  name: user.name,
                  user_id: user.id
                }
              }
            });

            if (signUpError && !signUpError.message.includes('already registered')) {
              console.error(`Error creating auth user ${user.email}:`, signUpError);
            }
          } catch (authError) {
            console.log(`Auth user ${user.email} might already exist`);
          }

        } catch (userError) {
          console.error(`Error initializing ${user.email}:`, userError);
        }
      }

      console.log('System users initialized successfully');
    } catch (error) {
      console.error('Error initializing system users:', error);
    }
  }
}; 